import { NgModule, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { SharedModule } from './common/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './common/components/header/header.component';
import { FooterComponent } from './common/components/footer/footer.component';
import { LocalStorageService } from './common/services/local-storage.service';
import { TokenInterceptor } from './common/interceptors/token.interceptor';
import { ClarityInterceptor } from './common/interceptors/clarity.interceptor';
import { KycTokenInterceptor } from './kyc/kyc-token.interceptor';
import { KycMockInterceptor } from './kyc/kyc-mock.interceptor';
import { AuthService } from './common/services/auth.service';
import { TranslationService } from './common/services/translation.service';
import { EnvServiceProvider } from './env.service.provider';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  NgxUiLoaderModule,
  NgxUiLoaderConfig,
  NgxUiLoaderHttpModule,
  NgxUiLoaderService,
} from 'ngx-ui-loader';
import { VerifyPaymentComponent } from './common/components/verify-payment/verify-payment.component';
import { DatePipe } from '@angular/common';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { UserIdleComponent } from './common/components/user-idle/user-idle.component';
import { SharedSignalsService } from './common/services/shared-signals.service';
import { SiteHeaderComponent } from './common/components/site-header/site-header.component';
import { SliderModule } from 'primeng/slider';
import { EnvService } from './env.service';
import { PressReleaseComponent } from './press-release/press-release.component';
import { PartnerSignupComponent } from './partners/partner-signup/partner-signup.component';
import { ShopsComponent } from './partners/shops/shops.component';
import { MerchantHomeComponent } from './partners/merchant-home/merchant-home.component';
import { PartnerFaqComponent } from './partners/merchant-home/partner-faq/partner-faq.component';
import { PartnerBannerComponent } from './partners/merchant-home/partner-banner/partner-banner.component';
import { TrustedParternsComponent } from './partners/merchant-home/trusted-parterns/trusted-parterns.component';
import { TalyIntegrationComponent } from './partners/merchant-home/taly-integration/taly-integration.component';
import { SellWithTalyComponent } from './partners/merchant-home/sell-with-taly/sell-with-taly.component';
import { WithoutTalyComponent } from './partners/merchant-home/taly-payment-option/without-taly/without-taly.component';
import { WithTalyComponent } from './partners/merchant-home/taly-payment-option/with-taly/with-taly.component';
import { OrderDetailsComponent } from './partners/merchant-home/taly-payment-option/order-details/order-details.component';
//import { PaymentMethodComponent } from './checkout/components/choose-payment-method/payment-method/payment-method.component';
import { RequestFormComponent } from './partners/partner-signup/request-form/request-form.component';
import { BusinessWithTalyComponent } from './partners/partner-signup/business-with-taly/business-with-taly.component';
import { TalyPaymentOptionComponent } from './partners/merchant-home/taly-payment-option/taly-payment-option.component';
import { PaymentMethodComponent } from './partners/merchant-home/taly-payment-option/payment-method/payment-method.component';
import { ShariaBoardComponent } from './sharia-board/sharia-board.component';
import { CompleteApplicationComponent } from './partners/complete-application/complete-application.component';
import { PageUnderMaintenanceComponent } from './page-under-maintenance/page-under-maintenance.component';
import { CustomerComplaintsComponent } from './customer-complaints/customer-complaints.component';

const envService = new EnvService();

const API = envService.ApiUrl;

const spinnerConfig: NgxUiLoaderConfig = {
  bgsColor: '#0E6FF7',
  bgsOpacity: 0.2,
  bgsPosition: 'center-center',
  bgsSize: 40,
  bgsType: 'circle',
  fastFadeOut: true,
  fgsColor: '#0E6FF7',
  fgsPosition: 'center-center',
  fgsSize: 40,
  fgsType: 'circle',
  logoPosition: 'center-center',
  // "logoUrl": "assets/images/taly-dark-icon.svg",
  overlayBorderRadius: '0',
  overlayColor: 'rgb(255,255,255)',
  pbColor: '#0E6FF7',
  hasProgressBar: false,
  text: '',
  textColor: '#FFFFFF',
  textPosition: 'center-center',
};
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    VerifyPaymentComponent,
    UserIdleComponent,
    SiteHeaderComponent,
    PressReleaseComponent,
    PartnerSignupComponent,
    ShopsComponent,
    MerchantHomeComponent,
    PartnerFaqComponent,
    PartnerBannerComponent,
    TalyPaymentOptionComponent,
    TrustedParternsComponent,
    TalyIntegrationComponent,
    SellWithTalyComponent,
    WithoutTalyComponent,
    WithTalyComponent,
    OrderDetailsComponent,
    PaymentMethodComponent,
    RequestFormComponent,
    BusinessWithTalyComponent,
    ShariaBoardComponent,
    CompleteApplicationComponent,
    PageUnderMaintenanceComponent,
    CustomerComplaintsComponent
  ],
  exports: [SharedModule],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    SliderModule,
    FormsModule,
    NgIdleKeepaliveModule.forRoot(),
    NgxUiLoaderHttpModule.forRoot({
      showForeground: true,
      exclude: [
        `${API}/accounts/profile/retrieve-charge/`,
        `${API}/accounts/payment/status`,
        `${API}/accounts/signup/nationalId/status`,
        `${API}/accounts/payment/order/status/`,
        `${API}/accounts/payment/processOrderPayment`,
        `${API}/merchants/merchantProfile/v1/limited`,
        `${API}/accounts/signup/cinet/authentication/status`,
        `${API}/accounts/signup/token/otp`,
        `${API}/accounts/profile/creditCard`,
        `${API}/accounts/profile/applepay/initiate/session`,
        `${API}/accounts/credit-validation/validate/status`,
        `${API}/accounts/credit-validation/validate`,
        `${API}/accounts/profile/cinet/authentication/renewal/status`,
        `${API}/accounts/payment/validate-order-value`,
        `${API}/accounts/payment/plans/v2`,
        `${API}/accounts/payment/info`,
        `${API}/accounts/faq`,
        `${API}/accounts/signup/resendOtp`,
        `${API}/accounts/signup/verifyOtp`,
        `${API}/uaa/oauth/token`,
        `${API}/accounts/profile`,
        `${API}/accounts/signup/customerInfo`,
        `${API}/accounts/profile/creditCard/verify`,
        `${API}/accounts/signup/init`,
        `${API}/accounts/signup/verifyNationalId`,
        `${API}/accounts/signup/cinet/authentication`,
        `${API}/accounts/maintenance`,
        `${API}/accounts/api/payment-links`,
        `${API}/backoffice/taly-policy`,
        `${API}/accounts/policy-acceptance`,
        `${API}/accounts/payment/v2/payInstallment/knet`,
        `${API}/merchants/interestRequest/submit`,
        `${API}/merchants/interestRequest`,
      ],
    }),
    NgxUiLoaderModule.forRoot(spinnerConfig),
    TranslateModule.forRoot({
      defaultLanguage: 'en-US',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    LocalStorageService,
    EnvServiceProvider,
    DatePipe,
    NgxUiLoaderService,
    AuthService,
    SharedSignalsService,
    EnvService,
    { provide: HTTP_INTERCEPTORS, useClass: KycTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: KycMockInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ClarityInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  private translationService = inject(TranslationService);

  constructor() {
    this.translationService.init();
  }
}
